import { courseStatus, dayMedium, dayShort, lastVisit, slotItems, upcomingAppointments, weeksSince } from './account'
import type { Account, PlanItem } from '../core/types/account'

export type AskTopic = 'routine' | 'reactie' | 'levering' | 'afspraak' | 'anders'

export const askTopics: { key: AskTopic; label: string; opener: string }[] = [
  { key: 'routine', label: 'Mijn routine', opener: 'ik heb een vraag over mijn routine' },
  { key: 'reactie', label: 'Hoe mijn huid reageert', opener: 'mijn huid reageert en ik wil even checken of dat normaal is' },
  { key: 'levering', label: 'Levering of bestelling', opener: 'ik heb een vraag over mijn bestelling' },
  { key: 'afspraak', label: 'Afspraak', opener: 'ik heb een vraag over mijn afspraak' },
  { key: 'anders', label: 'Iets anders', opener: 'ik heb een vraag' },
]

/** Alleen onderwerpen die voor deze klant zin hebben — geen "levering" als ze nooit bestelde. */
export const topicsFor = (account: Account, items: PlanItem[]) =>
  askTopics.filter((t) => {
    if (t.key === 'levering') return account.orders.length > 0 || Boolean(account.subscription)
    if (t.key === 'routine') return items.length > 0
    if (t.key === 'afspraak') return account.appointments.length > 0 || account.visits.length > 0
    return true
  })

const FALLBACK_TOPIC = askTopics[askTopics.length - 1]

/**
 * Het gekozen onderwerp, of anders het eerste dat voor haar zin heeft.
 * "Hoe mijn huid reageert" en "Iets anders" gelden altijd, dus er is altijd een onderwerp.
 */
export const pickTopic = (topics: typeof askTopics, key: AskTopic) =>
  topics.find((t) => t.key === key) ?? topics[0] ?? FALLBACK_TOPIC

export type AskContextLine = { label: string; value: string }

type Lookup = { productName: (id: string) => string; routineName?: string; treatmentName: (slug: string) => string }

/** Het "dossier" dat meegaat, zodat Sarah niets hoeft op te zoeken. Alleen wat er is. */
export function askContext(account: Account, items: PlanItem[], provisional: boolean, topic: AskTopic, lookup: Lookup): AskContextLine[] {
  const { customer, plan, subscription, orders, appointments } = account
  const lines: AskContextLine[] = [
    { label: 'Klant', value: `${`${customer.firstName} ${customer.lastName}`.trim()} (${customer.id})` },
  ]

  if (items.length && (topic === 'routine' || topic === 'reactie' || topic === 'anders')) {
    lines.push({
      label: 'Huidplan',
      value: provisional
        ? 'Nog geen persoonlijk plan, routine op basis van bestelling'
        : `${lookup.routineName ?? 'Persoonlijk plan'}, week ${weeksSince(plan.startedOn) + 1}`,
    })
    const name = (i: PlanItem) => `${lookup.productName(i.productId)}${i.frequency ? ` (${i.frequency})` : ''}`
    const am = slotItems(items, 'ochtend').map(name)
    const pm = slotItems(items, 'avond').map(name)
    if (am.length) lines.push({ label: 'Ochtend', value: am.join(' → ') })
    if (pm.length) lines.push({ label: 'Avond', value: pm.join(' → ') })
    const newest = [...items].sort((a, b) => b.startedOn.localeCompare(a.startedOn))[0]
    if (newest && weeksSince(newest.startedOn) <= 6)
      lines.push({ label: 'Nieuwst', value: `${lookup.productName(newest.productId)}, sinds ${dayShort(newest.startedOn)}` })
  }

  if (topic === 'levering' || topic === 'anders') {
    const last = [...orders].sort((a, b) => b.date.localeCompare(a.date))[0]
    if (last) lines.push({ label: 'Laatste bestelling', value: `#${last.id} (${dayShort(last.date)}, ${last.status.toLowerCase()})` })
    if (subscription)
      lines.push({
        label: 'Abonnement',
        value: subscription.status === 'actief' ? `volgende levering ${dayShort(subscription.nextDelivery)}` : subscription.status,
      })
  }

  if (topic === 'afspraak' || topic === 'reactie' || (topic === 'anders' && !items.length)) {
    const next = upcomingAppointments(appointments)[0]
    if (next) lines.push({ label: 'Afspraak', value: `${lookup.treatmentName(next.treatmentSlug)}, ${dayMedium(next.date)} ${next.time}` })
    const visit = lastVisit(account)
    if (visit) lines.push({ label: 'Laatste behandeling', value: `${lookup.treatmentName(visit.treatmentSlug)}, ${dayShort(visit.date)}` })
    const cs = courseStatus(account)
    if (cs && !cs.finished) lines.push({ label: 'Kuur', value: `${lookup.treatmentName(cs.course.treatmentSlug)}, ${cs.course.done} van ${cs.course.total}` })
  }
  return lines
}

export const askMessage = (firstName: string, founderFirstName: string, opener: string, context: AskContextLine[], question: string) =>
  [`Hi ${founderFirstName}, ${opener}.`, question.trim(), '', '— Mijn dossier —', ...context.map((l) => `${l.label}: ${l.value}`), '', `Groet, ${firstName}`]
    .filter((l, i, arr) => !(l === '' && (i === 0 || arr[i - 1] === '')))
    .join('\n')
